"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { CollectionOfferItem } from "@/features/collections/types";
import { avatarOf } from "@/features/home-v2/tiles";
import { sanitizeUrl } from "@/lib/helpers";
import { Avatar, StatePanel } from "@/features/home-v2/ui";
import { OFFERS_PAGE_SIZE, useOffersPage } from "../data/useDetailData";
import Pager from "../Pager";
import FitHex from "../FitHex";
import { dateTime, RetryButton, RowsSkeleton, tokenAmount, tokenDigits, TxLink } from "../parts";
type Props = {
  contract: string;
  lang: "fa" | "en";
  currency: string;
  t: Dictionary["collectionPage"];
  states: Dictionary["states"];
  emptyAction: ReactNode;
};

/**
 * THE OFFERS AS A BID BOARD — `useOffersPage`, the legacy feed unchanged
 * (read-only on-chain bids, the chain's own currency, the derived last page,
 * the failed-page-change test). The price leads each line, because it is
 * what a bid is; the token, marketplace, who → whom, when and the
 * transaction follow.
 */
export default function VitrineOffers(t0) {
  const $ = _c(54);
  const {
    contract,
    lang,
    currency,
    t,
    states,
    emptyAction
  } = t0;
  const isFa = lang === "fa";
  const locale = isFa ? "fa-IR" : "en-US";
  const o = useOffersPage(contract, lang);
  const Toward = isFa ? ArrowLeft : ArrowRight;
  if (o.isLoading && o.offers.length === 0) {
    let t1;
    if ($[0] !== states.loading) {
      t1 = <RowsSkeleton label={states.loading} rows={OFFERS_PAGE_SIZE} />;
      $[0] = states.loading;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (o.offers.length === 0) {
    let t1;
    if ($[2] !== emptyAction || $[3] !== o.isSuccess || $[4] !== o.retry || $[5] !== states || $[6] !== t) {
      t1 = o.isSuccess ? <StatePanel title={t.offersEmptyTitle} body={t.offersEmptyBody} action={emptyAction} /> : <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={o.retry} />} />;
      $[2] = emptyAction;
      $[3] = o.isSuccess;
      $[4] = o.retry;
      $[5] = states;
      $[6] = t;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t1;
  if ($[8] !== isFa || $[9] !== locale || $[10] !== o.total) {
    t1 = o.total != null ? <p className="vt-count" aria-live="polite"><b>{new Intl.NumberFormat(locale).format(o.total)}</b> {isFa ? "\u0622\u0641\u0631" : "offers"}</p> : null;
    $[8] = isFa;
    $[9] = locale;
    $[10] = o.total;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const t2 = t.offersTableLabel;
  const t3 = o.isFetching || undefined;
  let t4;
  if ($[12] !== Toward || $[13] !== contract || $[14] !== currency || $[15] !== lang || $[16] !== locale || $[17] !== o.offers || $[18] !== t.columnOfferer || $[19] !== t.columnSeller || $[20] !== t.columnToken || $[21] !== t.viewTransaction) {
    let t5;
    if ($[23] !== Toward || $[24] !== contract || $[25] !== currency || $[26] !== lang || $[27] !== locale || $[28] !== t.columnOfferer || $[29] !== t.columnSeller || $[30] !== t.columnToken || $[31] !== t.viewTransaction) {
      t5 = (b, i) => {
        const amount = b.price_eth ?? b.price_pol ?? null;
        const shown = tokenAmount(amount, locale);
        return <li key={b.id ?? b.tx_hash ?? `${b.token_id}-${i}`} className="vt-bid"><span className="vt-bid-price">{shown ? <><b>{shown}</b><small>{currency}</small></> : amount ?? "\u2014"}</span><span className="vt-bid-meta"><OfferWork offer={b} contract={contract} lang={lang} label={t.columnToken} /><span className="vt-market">{b.marketplace}</span><time dateTime={b.timestamp}>{dateTime(b.timestamp, locale)}</time></span><span className="vt-bid-path"><span className="v2-sr-only">{t.columnOfferer}</span><Offerer offer={b} lang={lang} /><Toward aria-hidden={true} className="vt-ev-arrow" /><span className="v2-sr-only">{t.columnSeller}</span><FitHex value={b.seller} /></span><TxLink chain={b.chain} hash={b.tx_hash} label={t.viewTransaction} /></li>;
      };
      $[23] = Toward;
      $[24] = contract;
      $[25] = currency;
      $[26] = lang;
      $[27] = locale;
      $[28] = t.columnOfferer;
      $[29] = t.columnSeller;
      $[30] = t.columnToken;
      $[31] = t.viewTransaction;
      $[32] = t5;
    } else {
      t5 = $[32];
    }
    t4 = o.offers.map(t5);
    $[12] = Toward;
    $[13] = contract;
    $[14] = currency;
    $[15] = lang;
    $[16] = locale;
    $[17] = o.offers;
    $[18] = t.columnOfferer;
    $[19] = t.columnSeller;
    $[20] = t.columnToken;
    $[21] = t.viewTransaction;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[33] !== t.offersTableLabel || $[34] !== t3 || $[35] !== t4) {
    t5 = <ul className="vt-bids" aria-label={t2} data-busy={t3}>{t4}</ul>;
    $[33] = t.offersTableLabel;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  let t6;
  if ($[37] !== isFa || $[38] !== o.isFetching || $[39] !== o.lastPage || $[40] !== o.page || $[41] !== o.setPage) {
    t6 = <Pager page={o.page} last={o.lastPage} busy={o.isFetching} onPage={o.setPage} isFa={isFa} />;
    $[37] = isFa;
    $[38] = o.isFetching;
    $[39] = o.lastPage;
    $[40] = o.page;
    $[41] = o.setPage;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== o.backgroundFailure || $[44] !== o.retry || $[45] !== states) {
    t7 = o.backgroundFailure ? <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={o.retry} />} /> : null;
    $[43] = o.backgroundFailure;
    $[44] = o.retry;
    $[45] = states;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== t6 || $[48] !== t7) {
    t8 = <div className="v2-cd-foot">{t6}{t7}</div>;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
  } else {
    t8 = $[49];
  }
  let t9;
  if ($[50] !== t1 || $[51] !== t5 || $[52] !== t8) {
    t9 = <div className="vt-stack">{t1}{t5}{t8}</div>;
    $[50] = t1;
    $[51] = t5;
    $[52] = t8;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  return t9;
}

/**
 * The work the offer is on: its picture and name, sent with the row (`nft`) so nothing is
 * fetched per line; a record without it keeps the token number.
 */
function OfferWork(t0) {
  const $ = _c(26);
  const {
    offer,
    contract,
    lang,
    label
  } = t0;
  const isFa = lang === "fa";
  const tokenId = offer.nft?.token_id ?? offer.token_id;
  const href = `/${lang}/assets/${offer.chain || "ethereum"}/${offer.nft?.contract_address || offer.contract_address || contract}/${tokenId}`;
  let t1;
  if ($[0] !== offer.nft?.name) {
    t1 = offer.nft?.name?.trim() || null;
    $[0] = offer.nft?.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  let t2;
  if ($[2] !== offer.nft?.image) {
    t2 = sanitizeUrl(offer.nft?.image ?? undefined) || null;
    $[2] = offer.nft?.image;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const image = t2;
  if (!name && !image) {
    const t3 = `${label} ${tokenId}`;
    let t4;
    if ($[4] !== isFa || $[5] !== tokenId) {
      t4 = tokenDigits(tokenId, isFa);
      $[4] = isFa;
      $[5] = tokenId;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== href || $[8] !== t3 || $[9] !== t4) {
      t5 = <Link className="vt-bid-token v2-focus" href={href} aria-label={t3}>#{t4}</Link>;
      $[7] = href;
      $[8] = t3;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  const t3 = `#${tokenId}`;
  let t4;
  if ($[11] !== image) {
    t4 = image ? <Image src={image} alt="" fill={true} sizes="28px" /> : null;
    $[11] = image;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <span className="vt-bid-thumb">{t4}</span>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== isFa || $[16] !== name || $[17] !== tokenId) {
    t6 = name ?? `#${tokenDigits(tokenId, isFa)}`;
    $[15] = isFa;
    $[16] = name;
    $[17] = tokenId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <span>{t6}</span>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== href || $[22] !== t3 || $[23] !== t5 || $[24] !== t7) {
    t8 = <Link className="vt-bid-nft v2-focus" href={href} title={t3}>{t5}{t7}</Link>;
    $[21] = href;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}

/** An NFTBaz offer names its account (with its avatar, or its initial); a record from another market only its wallet. */
function Offerer(t0) {
  const $ = _c(20);
  const {
    offer,
    lang
  } = t0;
  const u = offer.user && !Array.isArray(offer.user) ? offer.user as {
    name?: string | null;
    username?: string | null;
  } : null;
  let t1;
  if ($[0] !== u?.name || $[1] !== u?.username) {
    t1 = u?.name?.trim() || u?.username?.trim() || null;
    $[0] = u?.name;
    $[1] = u?.username;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const name = t1;
  if (!name) {
    let t2;
    if ($[3] !== offer.offerer) {
      t2 = <FitHex value={offer.offerer} />;
      $[3] = offer.offerer;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== u) {
    t2 = avatarOf(u);
    $[5] = u;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== name || $[8] !== t2) {
    t3 = <Avatar name={name} src={t2} size={22} />;
    $[7] = name;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== name) {
    t4 = <span>{name}</span>;
    $[10] = name;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const body = t5;
  let t6;
  if ($[15] !== body || $[16] !== lang || $[17] !== offer.offerer || $[18] !== u) {
    t6 = u?.username ? <Link href={`/${lang}/artists/${encodeURIComponent(u.username)}`} className="vt-bid-who v2-focus" title={offer.offerer}>{body}</Link> : <span className="vt-bid-who" title={offer.offerer}>{body}</span>;
    $[15] = body;
    $[16] = lang;
    $[17] = offer.offerer;
    $[18] = u;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}